import React, { useState, useEffect, useRef } from 'react';
import { 
  Calendar, Clock, User, Mail, Phone, MessageSquare, 
  ChevronDown, CheckCircle, ExternalLink, Instagram,
  Linkedin, Globe, ArrowRight, CalendarPlus, Facebook, Upload, Camera
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import './MeetingScheduler.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// X (Twitter) icon component
const XIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

// Team members data
const TEAM_MEMBERS = [
  {
    id: 'sagar-malik',
    name: 'Sagar Malik',
    role: 'Chief Executive Officer',
    email: 'sagar@bhyyve.com',
    phone: '+1 905-495-8888',
    photo: null, // Uploadable photo
    mascot: 'https://customer-assets.emergentagent.com/job_bee-booking/artifacts/k8zgjyqb_Pollen%20-%20bHyyve%20Mascot.png',
    social: {
      linkedin: 'https://www.linkedin.com/in/sagar-malik-8b98093aa/',
      x: 'https://x.com/bhyyve',
      instagram: 'https://www.instagram.com/bhyyve/',
      facebook: 'https://www.facebook.com/profile.php?id=61575853774007'
    },
    bio: 'Founder & CEO of bHyyve Technologies Inc.',
    availableSlots: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00', '17:00']
  }
  // Add more team members here
];

// Generate next 14 days for date selection
const generateDates = () => {
  const dates = [];
  const today = new Date();
  for (let i = 1; i <= 14; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    // Skip weekends
    if (date.getDay() !== 0 && date.getDay() !== 6) {
      dates.push(date);
    }
  }
  return dates;
};

const MeetingScheduler = () => {
  const [selectedMember, setSelectedMember] = useState(TEAM_MEMBERS[0]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [visitorTimezone, setVisitorTimezone] = useState('');
  const [timezoneOffset, setTimezoneOffset] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledDetails, setScheduledDetails] = useState(null);
  const [uploadedPhoto, setUploadedPhoto] = useState(null);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    reason: ''
  });

  const availableDates = generateDates();

  // Load uploaded photo from localStorage
  useEffect(() => {
    const savedPhoto = localStorage.getItem(`meetPhoto_${selectedMember.id}`);
    if (savedPhoto) {
      setUploadedPhoto(savedPhoto);
    }
  }, [selectedMember.id]);

  // Detect visitor's timezone
  useEffect(() => {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setVisitorTimezone(timezone);
      
      // Dynamically calculate Eastern Time offset (handles EST/EDT automatically)
      const now = new Date();
      const etString = now.toLocaleString('en-US', { timeZone: 'America/New_York' });
      const utcString = now.toLocaleString('en-US', { timeZone: 'UTC' });
      const etDate = new Date(etString);
      const utcDate = new Date(utcString);
      const easternOffset = (utcDate - etDate) / (60 * 1000); // in minutes, positive = behind UTC
      
      const localOffset = now.getTimezoneOffset(); // in minutes, positive = behind UTC
      setTimezoneOffset((easternOffset - localOffset) / 60); // difference in hours from ET to local
    } catch (e) {
      setVisitorTimezone('Unknown');
    }
  }, []);

  const convertToLocalTime = (estTime) => {
    // EST time string like "09:00" -> local time
    const [hours, minutes] = estTime.split(':').map(Number);
    const estDate = new Date();
    estDate.setHours(hours, minutes, 0, 0);
    
    // Adjust for timezone difference
    const localDate = new Date(estDate.getTime() + timezoneOffset * 60 * 60 * 1000);
    
    return localDate.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const handlePhotoUploadClick = () => {
    const password = window.prompt('Enter admin password to upload a photo:');
    if (password === 'bHyyve2026') {
      fileInputRef.current?.click();
    } else if (password !== null) {
      toast.error('Incorrect password');
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        setUploadedPhoto(base64);
        localStorage.setItem(`meetPhoto_${selectedMember.id}`, base64);
        toast.success('Photo uploaded successfully!');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const generateGoogleCalendarLink = (details) => {
    const { date, time, memberName, memberEmail, visitorName, reason } = details;
    
    // Parse the IST time
    const [hours, minutes] = time.split(':').map(Number);
    const startDate = new Date(date);
    startDate.setHours(hours, minutes, 0, 0);
    
    // End time is 30 minutes later
    const endDate = new Date(startDate.getTime() + 30 * 60 * 1000);
    
    // Format for Google Calendar (YYYYMMDDTHHMMSS)
    const formatDate = (d) => {
      return d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };
    
    const title = encodeURIComponent(`Meeting with ${memberName} - bHyyve`);
    const description = encodeURIComponent(`Meeting with ${visitorName}\n\nReason: ${reason}\n\nScheduled via bHyyve`);
    const location = encodeURIComponent('Virtual Meeting');
    
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${formatDate(startDate)}/${formatDate(endDate)}&details=${description}&location=${location}&add=${memberEmail}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedTime) {
      toast.error('Please select a date and time');
      return;
    }
    
    if (!formData.name || !formData.email || !formData.reason) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/schedule-meeting`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          teamMember: {
            id: selectedMember.id,
            name: selectedMember.name,
            email: selectedMember.email,
            role: selectedMember.role
          },
          visitor: {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            reason: formData.reason,
            timezone: visitorTimezone
          },
          meeting: {
            date: selectedDate.toISOString(),
            timeIST: selectedTime,
            localTime: convertToLocalTime(selectedTime)
          }
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setScheduledDetails({
          date: selectedDate,
          time: selectedTime,
          memberName: selectedMember.name,
          memberEmail: selectedMember.email,
          visitorName: formData.name,
          reason: formData.reason
        });
        setIsScheduled(true);
        toast.success('Meeting scheduled successfully!');
      } else {
        throw new Error('Failed to schedule');
      }
    } catch (error) {
      console.error('Error scheduling meeting:', error);
      toast.error('Failed to schedule meeting. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success Screen
  if (isScheduled && scheduledDetails) {
    const calendarLink = generateGoogleCalendarLink(scheduledDetails);
    
    return (
      <div className="meet-page meet-success-page" data-testid="meeting-success">
        <div className="meet-success-container">
          <div className="meet-success-icon">
            <CheckCircle size={80} />
          </div>
          <h1>Thank You for Scheduling!</h1>
          <p className="meet-success-subtitle">
            Your meeting with <strong>{scheduledDetails.memberName}</strong> has been confirmed.
          </p>
          
          <div className="meet-success-details">
            <div className="meet-detail-row">
              <Calendar size={20} />
              <span>{scheduledDetails.date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</span>
            </div>
            <div className="meet-detail-row">
              <Clock size={20} />
              <span>{scheduledDetails.time} EST{timezoneOffset !== 0 ? ` (${convertToLocalTime(scheduledDetails.time)} your time)` : ''}</span>
            </div>
          </div>
          
          <a 
            href={calendarLink} 
            target="_blank" 
            rel="noopener noreferrer"
            className="meet-calendar-btn"
          >
            <CalendarPlus size={20} />
            Add to Google Calendar
          </a>
          
          <div className="meet-followus">
            <p>Follow us on Instagram for updates!</p>
            <a 
              href="https://instagram.com/bhyyve" 
              target="_blank" 
              rel="noopener noreferrer"
              className="meet-instagram-btn"
            >
              <Instagram size={20} />
              @bhyyve
            </a>
          </div>
          
          <a href="/" className="meet-home-link">
            Return to Homepage
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="meet-page" data-testid="meeting-scheduler">
      {/* Header */}
      <header className="meet-header">
        <div className="meet-container">
          <a href="/" className="meet-logo-link">
            <img src="/logo.svg" alt="bHyyve" className="meet-logo" />
            <span className="meet-logo-text">bHyyve</span>
          </a>
          <div className="meet-timezone-badge">
            <Globe size={16} />
            <span>Your timezone: {visitorTimezone}</span>
          </div>
        </div>
      </header>

      <main className="meet-main">
        <div className="meet-container">
          <div className="meet-content">
            {/* Left Side - Team Member Profile */}
            <div className="meet-profile-section">
              <h1 className="meet-title">Schedule a Meeting</h1>
              <p className="meet-subtitle">Select a team member and book a time that works for you</p>
              
              {/* Team Member Dropdown */}
              <div className="meet-dropdown-container">
                <label>Meeting with</label>
                <button 
                  className="meet-dropdown-btn"
                  onClick={() => setShowDropdown(!showDropdown)}
                  data-testid="team-member-dropdown"
                >
                  <div className="meet-dropdown-selected">
                    <img src={uploadedPhoto || selectedMember.mascot} alt={selectedMember.name} className="meet-dropdown-avatar" />
                    <div>
                      <span className="meet-dropdown-name">{selectedMember.name}</span>
                      <span className="meet-dropdown-role">{selectedMember.role}</span>
                    </div>
                  </div>
                  <ChevronDown size={20} className={showDropdown ? 'rotated' : ''} />
                </button>
                
                {showDropdown && (
                  <div className="meet-dropdown-list">
                    {TEAM_MEMBERS.map(member => (
                      <button
                        key={member.id}
                        className={`meet-dropdown-item ${selectedMember.id === member.id ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedMember(member);
                          setShowDropdown(false);
                          setSelectedTime('');
                          // Load this member's photo
                          const savedPhoto = localStorage.getItem(`meetPhoto_${member.id}`);
                          setUploadedPhoto(savedPhoto || null);
                        }}
                      >
                        <img src={member.mascot} alt={member.name} className="meet-dropdown-avatar" />
                        <div>
                          <span className="meet-dropdown-name">{member.name}</span>
                          <span className="meet-dropdown-role">{member.role}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Selected Member Card */}
              <div className="meet-member-card">
                <div className="meet-photos-container">
                  {/* Uploadable Profile Photo */}
                  <div className="meet-profile-photo-wrapper">
                    <img 
                      src={uploadedPhoto || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(selectedMember.name) + '&background=fbbf24&color=1a1a1a&size=200'} 
                      alt={selectedMember.name} 
                      className="meet-member-photo" 
                    />
                    <button 
                      className="meet-photo-upload-btn"
                      onClick={handlePhotoUploadClick}
                      title="Upload photo"
                    >
                      <Camera size={16} />
                    </button>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handlePhotoUpload}
                      accept="image/*"
                      style={{ display: 'none' }}
                    />
                  </div>
                  {/* Pollen Mascot */}
                  <img src={selectedMember.mascot} alt="Pollen" className="meet-mascot" />
                </div>
                
                <h2>{selectedMember.name}</h2>
                <p className="meet-member-role">{selectedMember.role}</p>
                <p className="meet-member-bio">{selectedMember.bio}</p>
                
                <div className="meet-member-contact">
                  <a href={`mailto:${selectedMember.email}`} className="meet-contact-item">
                    <Mail size={16} />
                    <span>{selectedMember.email}</span>
                  </a>
                  <a href={`tel:${selectedMember.phone}`} className="meet-contact-item">
                    <Phone size={16} />
                    <span>{selectedMember.phone}</span>
                  </a>
                </div>
                
                <div className="meet-member-social">
                  {selectedMember.social.linkedin && (
                    <a href={selectedMember.social.linkedin} target="_blank" rel="noopener noreferrer" className="meet-social-btn" title="LinkedIn">
                      <Linkedin size={18} />
                    </a>
                  )}
                  {selectedMember.social.x && (
                    <a href={selectedMember.social.x} target="_blank" rel="noopener noreferrer" className="meet-social-btn" title="X">
                      <XIcon size={18} />
                    </a>
                  )}
                  {selectedMember.social.instagram && (
                    <a href={selectedMember.social.instagram} target="_blank" rel="noopener noreferrer" className="meet-social-btn" title="Instagram">
                      <Instagram size={18} />
                    </a>
                  )}
                  {selectedMember.social.facebook && (
                    <a href={selectedMember.social.facebook} target="_blank" rel="noopener noreferrer" className="meet-social-btn" title="Facebook">
                      <Facebook size={18} />
                    </a>
                  )}
                </div>
              </div>
            </div>
            
            {/* Right Side - Scheduling Form */}
            <div className="meet-form-section">
              <form onSubmit={handleSubmit} className="meet-form">
                {/* Date Selection */}
                <div className="meet-form-group">
                  <label>
                    <Calendar size={18} />
                    Select Date
                  </label>
                  <div className="meet-dates-grid">
                    {availableDates.map((date, index) => (
                      <button
                        key={index}
                        type="button"
                        className={`meet-date-btn ${selectedDate?.toDateString() === date.toDateString() ? 'selected' : ''}`}
                        onClick={() => setSelectedDate(date)}
                      >
                        <span className="meet-date-day">{date.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                        <span className="meet-date-num">{date.getDate()}</span>
                        <span className="meet-date-month">{date.toLocaleDateString('en-US', { month: 'short' })}</span>
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Time Selection */}
                {selectedDate && (
                  <div className="meet-form-group">
                    <label>
                      <Clock size={18} />
                      Select Time (EST - Eastern Standard Time)
                    </label>
                    <div className="meet-times-grid">
                      {selectedMember.availableSlots.map((time, index) => (
                        <button
                          key={index}
                          type="button"
                          className={`meet-time-btn ${selectedTime === time ? 'selected' : ''}`}
                          onClick={() => setSelectedTime(time)}
                        >
                          <span className="meet-time-ist">{time} EST</span>
                          {timezoneOffset !== 0 && (
                            <span className="meet-time-local">{convertToLocalTime(time)} (You)</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Contact Info */}
                {selectedTime && (
                  <div className="meet-contact-form">
                    <div className="meet-form-group">
                      <label>
                        <User size={18} />
                        Your Name *
                      </label>
                      <Input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder="Enter your full name"
                        required
                        data-testid="visitor-name"
                      />
                    </div>
                    
                    <div className="meet-form-group">
                      <label>
                        <Mail size={18} />
                        Email Address *
                      </label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="your@email.com"
                        required
                        data-testid="visitor-email"
                      />
                    </div>
                    
                    <div className="meet-form-group">
                      <label>
                        <Phone size={18} />
                        Phone Number
                      </label>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        placeholder="+1 234 567 8900"
                        data-testid="visitor-phone"
                      />
                    </div>
                    
                    <div className="meet-form-group">
                      <label>
                        <MessageSquare size={18} />
                        Reason for Meeting *
                      </label>
                      <textarea
                        value={formData.reason}
                        onChange={(e) => handleInputChange('reason', e.target.value)}
                        placeholder="Please briefly describe the purpose of this meeting..."
                        required
                        rows={4}
                        data-testid="visitor-reason"
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="meet-submit-btn"
                      disabled={isSubmitting}
                      data-testid="schedule-btn"
                    >
                      {isSubmitting ? 'Scheduling...' : (
                        <>
                          Schedule Meeting
                          <ArrowRight size={20} />
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </main>
      
      {/* Footer */}
      <footer className="meet-footer">
        <div className="meet-container">
          <p>© 2026 bHyyve Technologies Inc. All times shown in EST (Eastern Standard Time)</p>
        </div>
      </footer>
    </div>
  );
};

export default MeetingScheduler;
